import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import safeproLogo from '../assets/safepro-logo.svg';
import { useTranslation } from 'react-i18next';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function validatePassword(pwd) {
    if (pwd.length < 12) return t('reset.hint');
    if (!/[A-Z]/.test(pwd)) return t('reset.hint');
    if (!/[0-9]/.test(pwd)) return t('reset.hint');
    if (!/[^A-Za-z0-9]/.test(pwd)) return t('reset.hint');
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const validationError = validatePassword(password);
    if (validationError) { setError(validationError); return; }
    if (password !== confirm) { setError(t('reset.mismatch')); return; }

    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      navigate('/kirjaudu', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || t('reset.failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <img src={safeproLogo} alt="Safe Digi" className="h-12 mx-auto mb-4" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h1 className="text-base font-semibold text-gray-900 mb-4">{t('reset.title')}</h1>

          {!token ? (
            <p className="text-sm text-red-600">{t('reset.invalidToken')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reset.newPassword')}</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-brand-600 focus:border-brand-600"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {t('reset.hint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reset.confirmPassword')}</label>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className="w-full rounded-lg border-gray-300 text-sm focus:ring-brand-600 focus:border-brand-600"
                />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-700 hover:bg-brand-900 text-white font-medium py-2.5 rounded-lg text-sm transition-colors disabled:opacity-60"
              >
                {loading ? t('reset.saving') : t('reset.submit')}
              </button>
            </form>
          )}

          <div className="mt-4 text-center">
            <Link to="/kirjaudu" className="text-sm text-brand-700 hover:underline">
              ← {t('reset.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
